import { apiClient } from "@/lib/api";
import type {
  ActivitySegment,
  FleetSummary,
  LiveVehicle,
  RoutePathResponse,
  TrackingEvent,
  VehicleLocation,
} from "@/types/tracking";

/**
 * Fetches the live snapshot of all vehicles for the current tenant.
 * GET /api/v1/tracking/vehicles/live/
 */
export async function getLiveVehicles(): Promise<LiveVehicle[]> {
  const { data } = await apiClient.get<LiveVehicle[]>(
    "/v1/tracking/vehicles/live/",
  );
  return data;
}

/**
 * Fetches the GPS location history for a specific vehicle.
 * GET /api/v1/tracking/locations/?vehicle_id=X&from=...&to=...
 */
export async function getLocationHistory(
  vehicleId: number,
  from?: string,
  to?: string,
): Promise<VehicleLocation[]> {
  const { data } = await apiClient.get<VehicleLocation[]>(
    "/v1/tracking/locations/",
    {
      params: {
        vehicle_id: vehicleId,
        ...(from !== undefined ? { from } : {}),
        ...(to !== undefined ? { to } : {}),
      },
    },
  );
  return data;
}

/**
 * Fetches the list of tracking events for a given date.
 * GET /api/v1/tracking/events/?date=YYYY-MM-DD
 */
export async function getTrackingEvents(
  date: string,
): Promise<TrackingEvent[]> {
  const response = await apiClient.get<TrackingEvent[]>(
    "/v1/tracking/events/",
    {
      params: { date },
    },
  );
  return response.data;
}

/**
 * Fetches the recorded GPS path for a specific route.
 * GET /api/v1/tracking/routes/{routeId}/path/
 */
export async function getRoutePath(
  routeId: number,
): Promise<RoutePathResponse> {
  const { data } = await apiClient.get<RoutePathResponse>(
    `/v1/tracking/routes/${routeId}/path/`,
  );
  return data;
}

/**
 * Fetches the fleet-wide counters shown in the tracking header (TASK-5035).
 * GET /api/v1/tracking/fleet-summary/
 *
 * Deliberately a separate call rather than a client-side sum over the live
 * list: summing a filtered or paginated list would let the header disagree
 * with the rows under it.
 */
export async function getFleetSummary(): Promise<FleetSummary> {
  const { data } = await apiClient.get<FleetSummary>(
    "/v1/tracking/fleet-summary/",
  );
  return data;
}

/**
 * Fetches one vehicle's day as ordered activity segments (TASK-5034) — the
 * data behind the expanded card's 08:00–20:00 strip.
 * GET /api/v1/tracking/vehicles/{vehicleId}/activity/?date=YYYY-MM-DD
 */
export async function getVehicleActivity(
  vehicleId: number,
  date: string,
): Promise<ActivitySegment[]> {
  const { data } = await apiClient.get<ActivitySegment[]>(
    `/v1/tracking/vehicles/${vehicleId}/activity/`,
    { params: { date } },
  );
  return data;
}
